import React, { useState, useEffect, useRef } from "react";
import { IconButton } from "@mui/material";
import { Remove } from "@mui/icons-material";
import patientService from "../../Services/patientService";
import toast from "react-hot-toast";
import productService from "../../Services/productService";
import distributorServices from "../../Services/distributorServices";
import moment from "moment/moment";
import lostProductService from "../../Services/PharmacylostProductService";
import { useNavigate } from "react-router-dom";
import AutoComplete from "react-select";
import { MdPerson, MdCalendarToday, MdPointOfSale, MdDelete } from "react-icons/md";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const AddLostProduct = () => {
  const [patients, setPatients] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [search, setSearch] = useState("");
  const [searchDistributor, setSearchDistributor] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [lostProduct, setLostProduct] = useState({
    customer_name: "",
    customer_age: "",
    customer_phone: "",
    amount: 0,
    date: moment().format("YYYY-MM-DD"),
    note: "",
  });
  const [products, setProducts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [medicinesOptions, setMedicinesOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const medicineRef = useRef();

  const handleSelectDistributor = (medicine) => {
    setLostProduct((p) => ({ ...p, supplier_id: medicine.id }));
    setSearchDistributor(medicine.name);
    setShowDropdown(false);
  };

  const getMedicines = async () => {
    try {
      const response = await productService.fetchMedicines();
      setMedicines(response.products);
    } catch (error) {
      toast.error("Error fetching Medicines");
    }
  };

  const getDistributors = async () => {
    try {
      const response = await distributorServices.fetchAll();
      setDistributors(response.distributors);
    } catch (error) {
      toast.error("Error fetching Distributors");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  useEffect(() => {
    if (medicines.length > 0) {
      let array = [];
      for (const medicine of medicines) {
        array.push({
          value: medicine.stock_id,
          label: `${medicine.medicine_name} (PKR ${medicine.sale_price} - ${formatDate(medicine.expiry_date)} Qty: ${medicine.quantity})`,
        });
      }
      setMedicinesOptions(array);
    }
  }, [medicines]);

  useEffect(() => {
    getMedicines();
    getDistributors();
    medicineRef.current.focus();
  }, []);

  const handleSelectMedicine = (id) => {
    let medicine = medicines.filter((m) => m.stock_id === id)[0];
    const newProduct = {
      medicine_id: medicine.id,
      itemCode: medicine.item_code,
      itemName: medicine.medicine_name,
      expiry_date: medicine.expiry_date,
      batch_no: medicine.batch_no,
      quantity: 1,
      available_quantity: medicine.quantity,
      pack: 1,
      box: 1,
      unit_price: medicine.unit_price || 0,
      packPrice: medicine.pack_price || 0,
      discount: parseFloat(medicine.discount_price),
      sale_price: medicine.sale_price,
      discount_price: (medicine.sale_price - medicine.discount_price) * 1 || 0,
      total_price: medicine.sale_price * 1 || 0,
      stock_id: medicine.stock_id,
    };
    setProducts([...products, newProduct]);
    setSearch("");
  };

  const handleRemovePurchase = (index) => {
    setProducts(products.filter((_, i) => i !== index));
  };

  const handleQuantity = (index, name, value) => {
    setProducts((products) => {
      const array = [...products];
      let q = array[index].quantity;
      let p = array[index].sale_price;
      let dp = array[index].discount;
      if (name === "quantity") {
        if (value <= 0 || isNaN(value)) {
          toast.error("Quantity must be greater than zero");
          return products;
        }
        q = value;
      } else if (name === "sale_price") {
        p = value;
      } else if (name === "discount") {
        dp = value;
      }
      array[index] = {
        ...array[index],
        [name]: value,
        discount_price: q * (p - dp),
        total_price: p * q,
      };
      return array;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!lostProduct.customer_name) { toast.error("Customer Name is required"); return; }
    if (!lostProduct.date) { toast.error("Date is required"); return; }
    if (products.length === 0) { toast.error("At least one medicine must be added"); return; }
    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      if (Number(product.quantity) > Number(product.available_quantity)) {
        toast.error(`Quantity for "${product.itemName}" exceeds available quantity (${product.available_quantity})`);
        return;
      }
    }
    let totalAmount = 0;
    for (let product of products) totalAmount += product.discount_price;
    let submit = { ...lostProduct };
    submit.amount = totalAmount;
    submit.products = products;
    try {
      setLoading(true);
      await lostProductService.create(submit);
      navigate("/admin/pharmacy/LostProducts/lostproduct");
      toast.success("Lost Product Recorded Successfully");
    } catch (error) {
      toast.error(
        error?.message || "Unable to record the lost product. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const getAvailableDiscounts = (salePrice, unitPrice) => {
    const profitPercentage = ((salePrice - unitPrice) / unitPrice) * 100;
    if (profitPercentage >= 15) return [0, 5, 4, 3];
    if (profitPercentage >= 11 && profitPercentage < 15) return [0, 4, 3];
    if (profitPercentage >= 8 && profitPercentage < 11) return [0, 3];
    if (profitPercentage >= 5 && profitPercentage < 8) return [0, 2];
    if (profitPercentage >= 0 && profitPercentage < 5) return [0, 1];
    return [0];
  };

  const handleDiscountChange = (index, selectedDiscount) => {
    setProducts((prevProducts) => {
      const updatedProducts = [...prevProducts];
      const product = updatedProducts[index];
      const quantity = Number(product.quantity) || 1;
      const salePrice = Number(product.sale_price) || 0;
      const discountPerUnit = (salePrice * selectedDiscount) / 100;
      const discountedUnitPrice = salePrice - discountPerUnit;
      const totalDiscount = discountPerUnit * quantity;
      const totalAfterDiscount = discountedUnitPrice * quantity;
      const totalPrice = salePrice * quantity;
      updatedProducts[index] = {
        ...product,
        discount_selected: selectedDiscount,
        discount: totalDiscount,
        discount_price: totalAfterDiscount,
        total_price: totalPrice,
      };
      return updatedProducts;
    });
  };

  const totalBeforeDiscount = products.reduce((sum, p) => sum + p.total_price, 0);
  const totalDiscount = products.reduce((sum, p) => sum + (p.discount || 0) * (p.quantity || 1), 0);
  const totalAfterDiscount = products.reduce((sum, p) => sum + (p.discount_price || 0), 0);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "0.75rem",
      border: state.isFocused ? "1.5px solid #1F2937" : "1.5px solid #dde4ed",
      boxShadow: state.isFocused ? "0 0 0 4px rgba(31,41,55,0.1)" : "none",
      padding: "2px 4px",
      fontSize: "14px",
      fontFamily: "'DM Sans', sans-serif",
      "&:hover": { borderColor: "#1F2937" },
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "13px",
      backgroundColor: state.isSelected ? "#1F2937" : state.isFocused ? "#f4f7fb" : "white",
      color: state.isSelected ? "white" : "#0a2540",
    }),
    placeholder: (base) => ({ ...base, color: "#b0bcc9" }),
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');
        .pos-input {
          background: #f8fafc;
          border: 1.5px solid #dde4ed;
          border-radius: 0.75rem;
          padding: 10px 14px;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          color: #0a2540;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          width: 100%;
        }
        .pos-input:focus {
          border-color: #1F2937;
          box-shadow: 0 0 0 4px rgba(31,41,55,0.1);
        }
        .pos-input::placeholder { color: #b0bcc9; }
        .qty-input {
          background: #f8fafc;
          border: 1.5px solid #dde4ed;
          border-radius: 0.5rem;
          padding: 6px 8px;
          font-size: 13px;
          font-family: 'DM Sans', sans-serif;
          color: #0a2540;
          outline: none;
          width: 64px;
          text-align: center;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .qty-input:focus {
          border-color: #1F2937;
          box-shadow: 0 0 0 3px rgba(31,41,55,0.1);
        }
        .disc-select {
          background: #f8fafc;
          border: 1.5px solid #dde4ed;
          border-radius: 0.5rem;
          padding: 5px 8px;
          font-size: 12px;
          font-family: 'DM Sans', sans-serif;
          color: #0a2540;
          outline: none;
          cursor: pointer;
          transition: border-color 0.2s;
        }
        .disc-select:focus { border-color: #1F2937; }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-up { animation: fadeUp 0.5s ease both; }
      `}</style>

      <div className="min-h-screen bg-[#f4f7fb] py-8 px-4 fade-up" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #1F2937, #111827)" }}>
                <MdPointOfSale size={18} className="text-white" />
              </div>
              <h1 className="text-[#0a2540] text-[26px]" style={{ fontFamily: "'DM Serif Display', serif" }}>
                Record Pharmacy Lost Products
              </h1>
            </div>
            <p className="text-[#8a9bb0] text-sm ml-12">Fill in the details and add medicines to record a product loss.</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-[#eef1f6] p-6 mb-6">
            <h2 className="text-[13px] font-semibold uppercase tracking-[0.8px] text-[#4a6080] mb-4">Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

              <div>
                <label className="block text-[11px] font-semibold tracking-[0.7px] uppercase text-[#4a6080] mb-1.5">
                  Month Name 
                </label>
                <div className="relative flex items-center">
                  <input
                    type="text"
                    className="pos-input pl-9"
                    placeholder="Enter Month Name"
                    onChange={(e) => setLostProduct((p) => ({ ...p, customer_name: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold tracking-[0.7px] uppercase text-[#4a6080] mb-1.5">
                  Date
                </label>
                <div className="relative flex items-center">
                  <input
                    type="date"
                    className="pos-input pl-9"
                    value={lostProduct.date}
                    onChange={(e) => setLostProduct((p) => ({ ...p, date: moment(e.target.value).format("YYYY-MM-DD") }))}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-[#eef1f6] p-6 mb-6">
            <h2 className="text-[13px] font-semibold uppercase tracking-[0.8px] text-[#4a6080] mb-4">Add Medicines</h2>
            <div className="w-full md:w-1/2">
              <AutoComplete
                ref={medicineRef}
                styles={selectStyles}
                options={medicinesOptions}
                onChange={(v) => handleSelectMedicine(v.value)}
                placeholder="Search medicines by name..."
              />
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-[#eef1f6] overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-[#eef1f6] flex items-center justify-between">
              <h2 className="text-[13px] font-semibold uppercase tracking-[0.8px] text-[#4a6080]">Lost Items</h2>
              {products.length > 0 && (
                <span className="text-[11px] font-semibold bg-[#f4f7fb] text-[#1F2937] border border-[#dde4ed] px-2.5 py-1 rounded-full">
                  {products.length} item{products.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                <thead>
                  <tr style={{ background: '#1F2937' }}>
                    {["Sr.No", "Item Code", "Item Name", "Batch No.", "Box", "Pack", "Units", "Avail. Qty", "Expiry", "Sale Price", "Disc. %", "Disc. Amt", "Price/Unit", "Total", ""].map((h) => (
                      <th key={h} className="px-3 py-3 text-[11px] font-semibold uppercase tracking-[0.6px] text-white whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.length > 0 ? (
                    products.map((product, index) => {
                      const discountAmount = (product.discount || 0).toFixed(2);
                      const discountedUnitPrice = (product.discount_price / (product.quantity || 1)).toFixed(2);
                      const totalAfterDiscount = (product.discount_price || 0).toFixed(2);

                      return (
                        <tr
                          key={index}
                          className={`text-[13px] border-b border-[#f0f4f8] transition-colors ${index % 2 === 0 ? "bg-white" : "bg-[#fafbfd]"} hover:bg-[#f4f7fb]`}
                        >
                          <td className="px-3 py-3 font-bold text-[#1F2937]">{index + 1}</td>
                          <td className="px-3 py-3 font-medium text-[#4a6080]">{product.itemCode}</td>
                          <td className="px-3 py-3 font-semibold text-[#0a2540] whitespace-nowrap">{product.itemName}</td>
                          <td className="px-3 py-3 text-[#4a6080]">{product.batch_no}</td>
                          <td className="px-3 py-3 text-[#4a6080]">{product.box}</td>
                          <td className="px-3 py-3 text-[#4a6080]">{product.pack}</td>
                          <td className="px-3 py-3">
                            <input
                              value={product.quantity}
                              type="number"
                              name="quantity"
                              className="qty-input"
                              onChange={(e) => {
                                const entered = parseInt(e.target.value, 10);
                                if (entered > product.available_quantity) {
                                  toast.error("Entered quantity exceeds available quantity!");
                                } else {
                                  handleQuantity(index, e.target.name, e.target.value);
                                }
                              }}
                            />
                          </td>
                          <td className="px-3 py-3">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[12px] font-semibold bg-orange-50 text-orange-600 border border-orange-200">
                              {product.available_quantity}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-[#4a6080] whitespace-nowrap">{product.expiry_date}</td>
                          <td className="px-3 py-3 font-semibold text-[#0a2540]">{product.sale_price}</td>
                          <td className="px-3 py-3">
                            <select
                              className="disc-select"
                              value={product.discount_selected || ""}
                              onChange={(e) => handleDiscountChange(index, parseFloat(e.target.value))}
                            >
                              {getAvailableDiscounts(product.sale_price, product.unit_price).map((d) => (
                                <option key={d} value={d}>{d > 0 ? `${d}%` : "0"}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-3 font-semibold text-[#1F2937]">Rs. {discountAmount}</td>
                          <td className="px-3 py-3 text-[#4a6080]">{discountedUnitPrice}</td>
                          <td className="px-3 py-3 font-bold text-[#0a2540]">Rs. {totalAfterDiscount}</td>
                          <td className="px-3 py-3">
                            <button
                              type="button"
                              onClick={() => handleRemovePurchase(index)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 transition-colors"
                            >
                              <MdDelete size={15} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="15" className="text-center py-12 text-[#b0bcc9]">
                        <div className="flex flex-col items-center gap-2">
                          <MdPointOfSale size={32} className="text-[#dde4ed]" />
                          <span className="text-sm">No medicines added yet. Search above to add items.</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-[#eef1f6] p-6">
            <h2 className="text-[13px] font-semibold uppercase tracking-[0.8px] text-[#4a6080] mb-5">Summary</h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <SummaryCard label="Total Medicines" value={products.length} unit="" color="#0a2540" bg="#f4f7fb" />
              <SummaryCard label="Total Amount" value={`Rs. ${totalBeforeDiscount.toFixed(2)}`} color="#0a2540" bg="#f4f7fb" />
              <SummaryCard label="Total Discount" value={`Rs. ${totalDiscount.toFixed(2)}`} color="#e96f1f" bg="#fff7f0" />
              <SummaryCard label="Net Amount" value={`Rs. ${totalAfterDiscount.toFixed(2)}`} color="#1F2937" bg="#f4f7fb" highlight />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2.5 px-8 py-3.5 rounded-xl text-white text-[15px] font-semibold tracking-[0.3px] transition-all disabled:opacity-60 disabled:cursor-not-allowed hover:enabled:-translate-y-px"
                style={{
                  background: "linear-gradient(135deg, #1F2937 40%, #111827 100%)",
                  boxShadow: "0 4px 20px rgba(31,41,55,0.35)",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {loading ? (
                  <>
                    <AiOutlineLoading3Quarters size={17} className="animate-spin" />
                    Recording…
                  </>
                ) : (
                  <>
                    <MdPointOfSale size={18} />
                    Record Lost Product
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

    </>
  );
};
const SummaryCard = ({ label, value, color, bg, highlight }) => (
  <div
    className={`rounded-xl p-4 flex flex-col gap-1 ${highlight ? "border border-[#dde4ed]" : "border border-[#eef1f6]"}`}
    style={{ background: bg }}
  >
    <span className="text-[11px] font-semibold uppercase tracking-[0.7px] text-[#8a9bb0]">{label}</span>
    <span className="text-[18px] font-bold" style={{ color, fontFamily: "'DM Serif Display', serif" }}>{value}</span>
  </div>
);

export default AddLostProduct;
